import { Inject, Injectable } from '@angular/core';

import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { BehaviorSubject, Subject, filter, takeUntil } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';

import { UserAuthService } from './user-auth.service';
import { ApiCallService } from './web-serivces/api-call.service';
import { auth } from '../core/auth/auth-endpoint';
import { ProfileResponse } from '@components/profile/Interfaces/profile';
import { USER_GROUP_ID } from '@environments/user-group-ids';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isIframe = false;
  loginDisplay = false;

  tokens = {
    accessToken: '',
    idToken: '',
  };
  isAdmin = false;
  private hasTriggeredLogin = false;

  private readonly isAdminSubject = new BehaviorSubject<boolean | null>(null);
  isAdmin$ = this.isAdminSubject.asObservable();

  private readonly destroy$ = new Subject();
  private readonly userDetailsSubject = new BehaviorSubject<ProfileResponse['user'] | null>(null);
  userDetails$ = this.userDetailsSubject.asObservable();

  constructor(
    @Inject(MSAL_GUARD_CONFIG)
    private readonly msalGuardConfig: MsalGuardConfiguration,
    private readonly authService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly api: ApiCallService,
    private readonly userAuthSvc: UserAuthService,
    private readonly jwtHelper: JwtHelperService,
  ) {}

  // Method to get fresh tokens
async getTokens(): Promise<{ accessToken: string; idToken: string }> {
  let account = this.authService.instance.getActiveAccount();
  if (!account) {
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      this.authService.instance.setActiveAccount(account);
    }
  }
  try {
    const response: any =
      await this.authService.instance.acquireTokenSilent({
        account: account,
        // scopes: [environment.environment.graphApiScope]
        scopes: ['user.read']
      });
    const idToken = account?.idToken || response.idToken;
    return {
      accessToken: response.accessToken,
      idToken: idToken
    };
  } catch (error) {
 
    if (error instanceof InteractionRequiredAuthError) {
      this.loginRedirect();
    }
    throw error;
  }
}
 
  broadCast() {
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    // Ensure redirect response is processed explicitly so account / activeAccount is set
    // This avoids blank page / guards running with default state after a redirect sign-in.
    this.authService.instance
      .handleRedirectPromise()
      .then((result) => {
        console.log('redirect Complete', {
          result, accounts: this.authService.instance.getAllAccounts().length
        })
        if (result?.account) {
          this.authService.instance.setActiveAccount(result.account);
        }
        // Update display state after processing redirect
        this.setLoginDisplay();
      })
      .catch((err) => {
        console.error("MSAL Redirect Processing Failed:", err);
        // FORCE stop the loop: Set the flag to prevent MsalGuard/InitializeApp from redirecting again
       // this.hasTriggeredLogin = true; 
        // Safely clear the broken/unparseable Microsoft hash out of the address bar
        // const hash = window.location.hash;
        // if (hash && (hash.includes('id_token=') || hash.includes('code=') || hash.includes('state='))) {
        //   window.location.hash = "#/"; 
        // }
      });
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });
    // Handle InteractionStatus.None to initialize app
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        console.log('INPROGRESS NONE hasTriggeredLogin: INPROGRESS NONE Active:', this.hasTriggeredLogin , this.authService.instance.getActiveAccount());
        this.initializeApp();
      });
  }

  initializeApp() {
    this.checkAndSetActiveAccount(); 
    this.startIdTokenRefresh();
    // Check if the current URL still contains raw authentication tokens.
    // If it does, MSAL is actively processing it. Exit immediately to prevent race conditions.
    // const urlHash = window.location.hash;
    // if (urlHash && (urlHash.includes('id_token=') || urlHash.includes('code=') || urlHash.includes('state='))) {
    //   console.warn("MSAL Auth Hash detected in URL. Deferring initialization to protect against redirect loops.");
    //   return; 
    // }

    const accounts = this.authService.instance.getAllAccounts();

    console.log('Active account: hasTriggeredLogin:', this.authService.instance.getActiveAccount(), this.hasTriggeredLogin);
    if (accounts.length === 0) { 
      console.log('No account found - Not redirecting')
      // Instead of redirecting immediately, wait for user interaction 
      this.loginRedirectOnce(); // need to do commented// Use loginRedirectOnce to prevent looping
      return; 
    } else {
        this.setToken().then(() => { 
          const activeAccount = this.authService.instance.getActiveAccount();
          console.log('setToken success — activeAccount:', activeAccount);
          if (activeAccount) {
            this.loadUserDetails(activeAccount);
          }
          }) 
          .catch((error) => { 
             console.error("Token setup during initialization failed:", error);
          });
    }
  }

  loadUserDetails(activeAccount) {
    this.api
      .singleApiCall(auth.apiConfig.uri, 'GET', null, null, null, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.userAuthSvc.getUserDetails({
          id: activeAccount.localAccountId,
          name: activeAccount.name || '',
          username: activeAccount.username.split('@')[0],
          email: activeAccount.username,
          designation: user?.jobTitle || '',
        });
        this.userDetailsSubject.next(user);
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    this.userAuthSvc.isUserLoggedIn$.next(this.authService.instance.getAllAccounts().length > 0);
  }

  checkAndSetActiveAccount() {
    console.log('check active account', { active: this.authService.instance.getAllAccounts(), accounts: this.authService.instance.getActiveAccount() });
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  setToken() {
    // const request = { scopes: [environment.environment.graphApiScope] };
    const request = { scopes: ['user.read'] };
    return this.authService.instance
      .acquireTokenSilent(request)
      .then(({ accessToken, idToken }) => {
        this.tokens = { accessToken, idToken };
        localStorage.setItem('token', idToken);
        this.checkForAdmin();
      })
      .catch((error) => {
        const code = (error && (error.errorCode || error?.errorCode)) || '';
        console.error('ErrorCode:', code);
        console.error('Error:', error);
        console.error('isIframe:', this.isIframe);
        console.error('hasTriggeredLogin:', this.hasTriggeredLogin);
        if (code === 'no_account_error') {
          this.loginRedirectOnce();
          return;
        }
        if (code === 'interaction_required' || error instanceof InteractionRequiredAuthError) {
          if (this.isIframe) {
            // Do not perform redirects inside hidden background iframes
          } else {
            this.loginRedirectOnce();
          }
          return;
        }

        if (!this.isIframe) {
          console.warn('Unhandled token error, forcing re-login:', code);
          this.loginRedirectOnce();
        }
      });
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      localStorage.clear();
      sessionStorage.clear();
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginRedirectOnce() {
    console.log('hasTriggeredLogin', this.hasTriggeredLogin);
    if (this.hasTriggeredLogin) {
      return;
    }
    this.hasTriggeredLogin = true;
    this.loginRedirect();
  }

  logout() {
    this.authService.logoutRedirect();
  }

  destroy() {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  // Ensure admin flag is computed early (e.g., before AppComponent init)
  // Useful for route guards on new tabs/windows
  ensureAdminComputed() {
    const current = this.isAdminSubject.getValue();
    if (current !== null) return; // Already determined (true or false)

    const storedIdToken = localStorage.getItem('token');
    if (storedIdToken) {
      this.tokens.idToken = storedIdToken;
      this.checkForAdmin(); // This will eventually call .next(true) or .next(false)
    } else {
      // If there is no token at all, we know they aren't an admin
      this.isAdminSubject.next(false);
    }
  }
  checkForAdmin() {
    const idToken = this.jwtHelper.decodeToken(this.tokens.idToken);

    // Determine current environment from API base URL
    const baseURL = environment?.environment?.baseURL || '';
    let currentEnv: 'DEV' | 'SIT' | 'UAT' | 'PROD' = 'DEV'; // default

    if (baseURL.includes('-sit-')) {
      currentEnv = 'SIT';
    } else if (baseURL.includes('-uat-')) {
      currentEnv = 'UAT';
    } else if (
      baseURL.includes('-api.networkrail.co.uk') &&
      !baseURL.includes('-dev-') &&
      !baseURL.includes('-sit-') &&
      !baseURL.includes('-uat-')
    ) {
      currentEnv = 'PROD';
    }
    const adminGroupId = USER_GROUP_ID?.[currentEnv]?.['ADMIN'];
    if (idToken?.groups?.includes(adminGroupId)) {
      this.isAdmin = true;
    } else {
      this.isAdmin = false;
    }
    this.isAdminSubject.next(this.isAdmin);
  }

  startIdTokenRefresh() {
    setInterval(async () => {
      try {
        const account = this.authService.instance.getActiveAccount();
        if (!account) return;
          await this.authService.instance.acquireTokenSilent({
            account: account,
            // scopes: [environment.environment.graphApiScope],
            scopes: ['user.read'],
          });
      } catch (error) {
        console.error('IdToken refresh failed', error);
      }
    }, 50 * 60 * 1000); // 50 minutes; 

  }
 
}
